import React, { useRef, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../ui/input';
import { announceToScreenReader } from '../../lib/accessibility';

// Desktop Nav Button with better tap target and accessibility
const NavButton = ({ icon: Icon, onClick, badge, tooltip, isActive, testId, isDark, ariaLabel }) => {
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.();
    }
  };

  return (
    <button
      onClick={onClick}
      onKeyDown={handleKeyDown}
      data-testid={testId}
      title={tooltip}
      aria-label={ariaLabel || `${tooltip}${badge > 0 ? `, ${badge} unread` : ''}`}
      aria-current={isActive ? 'page' : undefined}
      role="button"
      tabIndex={0}
      className={`
        relative flex items-center justify-center
        w-11 h-11 rounded-xl
        transition-all duration-200
        active:scale-95
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400 focus-visible:ring-offset-2
        ${isActive 
          ? 'text-yellow-400 bg-yellow-400/10' 
          : isDark
            ? 'text-gray-300 hover:text-white hover:bg-white/10 active:bg-white/20'
            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 active:bg-gray-200'
        }
      `}
    >
      <Icon className={`w-5 h-5 ${isActive ? 'fill-yellow-400/20' : ''}`} strokeWidth={2} aria-hidden="true" />
      {badge > 0 && (
        <>
          <span 
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full px-1 shadow-lg"
            aria-hidden="true"
          >
            {badge > 99 ? '99+' : badge}
          </span>
          <span className="sr-only">{badge} unread notifications</span>
        </>
      )}
    </button>
  );
};

export const SearchBar = ({ isOpen, setIsOpen, isDark }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const formRef = useRef(null);

  // Focus input when search opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      announceToScreenReader('Search field opened. Type to search content.');
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const searchValue = inputRef.current?.value?.trim();
    if (searchValue) {
      navigate(`/search?q=${encodeURIComponent(searchValue)}`);
      setIsOpen(false);
      announceToScreenReader(`Searching for ${searchValue}`);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    announceToScreenReader('Search closed');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  if (isOpen) {
    return (
      <form 
        ref={formRef}
        onSubmit={handleSubmit} 
        className="flex items-center" 
        data-testid="search-form"
        role="search"
        aria-label="Search content"
      >
        <label htmlFor="header-search" className="sr-only">Search for content</label>
        <Input
          ref={inputRef}
          id="header-search"
          type="search"
          placeholder="Search..."
          className={`w-40 md:w-64 h-10 ${
            isDark 
              ? 'bg-gray-900 border-gray-700 text-white placeholder:text-gray-500'
              : 'bg-gray-100 border-gray-300 text-gray-900 placeholder:text-gray-400'
          }`}
          autoFocus
          autoComplete="off"
          onBlur={() => setTimeout(handleClose, 200)}
          onKeyDown={handleKeyDown}
          data-testid="search-input"
          aria-describedby="search-hint"
        />
        <span id="search-hint" className="sr-only">
          Press Enter to search, Escape to close
        </span>
        <button 
          type="button"
          onClick={handleClose}
          className={`ml-2 p-2 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400 ${
            isDark ? 'text-gray-400 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
          }`}
          data-testid="search-close"
          aria-label="Close search"
        >
          <X className="w-5 h-5" aria-hidden="true" />
        </button>
      </form>
    );
  }

  return (
    <NavButton
      icon={Search}
      onClick={() => setIsOpen(true)}
      tooltip="Search"
      ariaLabel="Open search"
      testId="search-btn"
      isDark={isDark}
    />
  );
};

export { NavButton };
export default SearchBar;
